import { api } from '@/utils/api';

export const useTeamMemberInviteMutation = () => {
  const utils = api.useUtils();

  return api.team.memberInvite.useMutation({
    onSuccess: async (_, { teamId }) => {
      await Promise.all([
        // refetch invited users
        utils.team.invitedTeamUsers.invalidate({ teamId }),
        // a user whose email is in out system is immediately
        // added to the team on invite, hence meed to invalidate teamUsers
        utils.team.teamUsers.invalidate({ teamId }),
        // need to invalidate to reload team member counts
        utils.team.id.invalidate({ teamId }),

        utils.team.all.invalidate(),
      ]);
    },
  });
};

export const useTeamMemberDeleteMutation = () => {
  const utils = api.useUtils();

  return api.team.memberDelete.useMutation({
    onSuccess: async (_, { teamId }) => {
      // refetch team users
      void utils.team.teamUsers.invalidate({ teamId });
      // need to invalidate to reload team member counts
      void utils.team.id.invalidate({ teamId });

      void utils.team.all.invalidate();
      await utils.user.me.invalidate();
    },
  });
};

export const useTeamMemberDeleteInvitationMutation = () => {
  const utils = api.useUtils();

  return api.team.inviteDelete.useMutation({
    onSuccess: async (_, { teamId }) => {
      await Promise.all([
        // refetch invited users
        utils.team.invitedTeamUsers.invalidate({ teamId }),
        // need to invalidate to reload team member counts
        utils.team.id.invalidate({ teamId }),

        utils.team.all.invalidate(),
      ]);
    },
  });
};

export const useTeamUpdateMutation = () => {
  const utils = api.useUtils();
  return api.team.update.useMutation({
    onSuccess: async ({ teamId, userId }) => {
      void utils.team.all.invalidate();
      void utils.user.me.invalidate();

      await Promise.all([utils.team.teamUser.invalidate({ teamId, userId }), utils.team.id.invalidate({ teamId })]);
    },
  });
};

export const useTeamUpdateImageMutation = () => {
  const utils = api.useUtils();
  return api.team.updateImage.useMutation({
    onSuccess: async (res) => {
      const teamId = res.teamId;
      const userId = res.userId;

      utils.user.me.setData(undefined, (data) => {
        if (data && data.userTeam && data.userTeam.teamId == teamId) {
          return {
            ...data,
            userTeam: {
              ...data.userTeam,
              teamLogo: res.teamLogo,
            },
          };
        }
        return data;
      });
      utils.team.teamUser.setData({ teamId, userId }, (data) => {
        if (data) {
          return {
            ...data,
            teamLogo: res.teamLogo,
          };
        }
        return data;
      });
      utils.team.id.setData({ teamId }, (data) => {
        if (data) {
          return {
            ...data,
            teamLogo: res.teamLogo,
          };
        }
        return data;
      });

      await utils.team.all.invalidate();
    },
  });
};

export const useTeamCreateWebhookMutation = () => {
  const utils = api.useUtils();
  return api.team.webhookCreate.useMutation({
    onSuccess: async (_, { teamId }) => {
      await Promise.all([
        utils.user.me.invalidate(),
        utils.team.all.invalidate(),
        utils.team.id.invalidate({ teamId }),
        utils.team.webhook.invalidate({ teamId }),
      ]);
    },
  });
};

export const useTeamUpdateNewApiMutation = () => {
  const utils = api.useUtils();
  return api.team.updateNewsApi.useMutation({
    onSuccess: async (_, { teamId }) => {
      await Promise.all([
        utils.user.me.invalidate(),
        utils.team.all.invalidate(),
        utils.team.id.invalidate({ teamId }),
        utils.team.newsApi.invalidate({ teamId }),
      ]);
    },
  });
};

export const useTeamUpdateGTMMutation = () => {
  const utils = api.useUtils();
  return api.team.updateGtm.useMutation({
    onSuccess: async (_, { teamId }) => {
      await Promise.all([
        utils.team.all.invalidate(),
        utils.team.id.invalidate({ teamId }),
        utils.team.gtm.invalidate({ teamId }),
      ]);
    },
  });
};

export const useTeamUpdateTwilioMutation = () => {
  const utils = api.useUtils();
  return api.team.updateTwilio.useMutation({
    onSuccess: async (_, { teamId }) => {
      await Promise.all([
        utils.team.all.invalidate(),
        utils.team.id.invalidate({ teamId }),
        utils.team.twilio.invalidate({ teamId }),
      ]);
    },
  });
};

export const useTeamRemoveSlackMutation = () => {
  const utils = api.useUtils();
  return api.team.removeSlack.useMutation({
    onSuccess: async (_, { teamId }) => {
      await Promise.all([
        utils.team.all.invalidate(),
        utils.team.id.invalidate({ teamId }),
        utils.team.slack.invalidate({ teamId }),
      ]);
    },
  });
};

export const useTeamDeleteMutation = () => {
  const utils = api.useUtils();
  return api.team.delete2.useMutation({
    onSuccess: async (res, { teamId }) => {
      // @TODO: fix
      //
      // if (res?.newDefaultTeamId) {
      //   utils.user.me.setData(undefined, (data) => {
      //     if (data?.user != null) {
      //       return {
      //         user: { ...data.user, defaultTeamId: res.newDefaultTeamId },
      //       };
      //     }
      //     return data;
      //   });
      // }

      //this causes a full page spinner, figure out how to fix it
      await Promise.all([
        utils.team.all.invalidate(),
        utils.user.me.invalidate(),
        utils.team.id.invalidate({ teamId }),
      ]);
    },
  });
};

export const useTeamCreateMutation = () => {
  const utils = api.useUtils();
  return api.team.create.useMutation({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onSuccess: async (res, { makeDefault }) => {
      void utils.team.all.invalidate();
      // @TODO: fix
      // if (makeDefault) {
      //   const defaultTeamId = res.teamId;
      //   utils.user.me.setData(undefined, (data) => {
      //     if (data?.user != null) {
      //       return {
      //         user: { ...data.user, defaultTeamId },
      //       };
      //     }
      //     return data;
      //   });
      // }
      await utils.user.me.invalidate();
    },
  });
};

export const useTeamBulkSetPremiumMutation = () => {
  const utils = api.useUtils();
  return api.superAdmin.bulkSetPremium.useMutation({
    onSuccess: async () => {
      await utils.superAdmin.allTeams.invalidate();
      await utils.superAdmin.teamId.invalidate();
    },
  });
};

export const useTeamUpdateUsersCountMutation = () => {
  const utils = api.useUtils();
  return api.superAdmin.updateUsersCount.useMutation({
    onSuccess: async () => {
      await utils.superAdmin.allTeams.invalidate();
      await utils.superAdmin.teamId.invalidate();
    },
  });
};

export const useTeamUpdateUser = () => {
  const utils = api.useUtils();
  return api.superAdmin.updateUser.useMutation({
    onSuccess: async () => {
      await utils.superAdmin.allTeams.invalidate();
      await utils.superAdmin.teamId.invalidate();
      await utils.superAdmin.allUsers.invalidate();
      await utils.superAdmin.userId.invalidate();
    },
  });
};

export const useTeamUpdateTeamDataMutation = () => {
  const utils = api.useUtils();
  return api.superAdmin.updateTeamData.useMutation({
    onSuccess: async () => {
      await utils.superAdmin.allTeams.invalidate();
      await utils.superAdmin.teamId.invalidate();
    },
  });
};

export const useTeamUpdateOnboardingMutation = () => {
  const utils = api.useUtils();
  return api.team.updateOnboarding.useMutation({
    onSuccess: async () => {
      await utils.team.onboarding.invalidate();
    },
  });
};
